import type { OrgSettings } from '@/hooks/useOrgSettings';
import { PRICING_DEFAULTS } from '@/hooks/useOrgSettings';

/**
 * Costo di base di un preventivo (somma di tutte le voci di costo PRIMA dei
 * mark-up). Le tre componenti sono separate perché applichiamo mark-up
 * diversi (i materiali tipicamente +25%, la manodopera +70%).
 */
export interface CostBase {
  /** Costo materiali + viti netto (€). */
  materials: number;
  /** Costo manodopera netto (€). */
  labor: number;
  /** Costo finitura (materiali finitura + labor finitura) netto (€). */
  finish: number;
  /** Superficie totale del preventivo, m². Serve per spalmare i forfait. */
  totalArea?: number;
  /** Dettaglio informativo: quanto di `materials` e` sfrido (€). */
  waste?: number;
  /** Dettaglio informativo: quanto di `materials` e` smaltimento (€). */
  disposal?: number;
}

/**
 * Risultato del calcolo prezzo vendita: scomposto per audit e per il PDF.
 * Tutti i valori sono in €.
 */
export interface SellingBreakdown {
  // ====== Costi base (input) ======
  costMaterials: number;
  costLabor: number;
  costFinish: number;
  costBase: number; // somma

  // ====== Mark-up sui costi diretti ======
  markupMaterials: number;
  markupLabor: number;
  markupFinish: number;
  markupTotal: number;

  // ====== Costi accessori (applicati DOPO i mark-up) ======
  overhead: number;     // % sul subtotale (cost + markup)
  safety: number;       // % sul subtotale
  transport: number;    // forfait
  disposal: number;     // forfait
  accessoriesTotal: number;

  // ====== Prezzo lordo (prima dello sconto cliente) ======
  grossPrice: number;   // costBase + markupTotal + accessoriesTotal

  // ====== Sconto cliente ======
  customerDiscountPct: number;
  customerDiscount: number;

  // ====== Imponibile (prezzo offerta IVA esclusa) ======
  netPrice: number;     // grossPrice - customerDiscount

  // ====== IVA ======
  /**
   * Aliquota IVA "rappresentativa" del preventivo. Se ci sono più aliquote
   * (vedi vatBreakdown), questa è la più ricorrente o la default org.
   */
  ivaPct: number;
  /** IVA totale aggregata (sum di tutti i gruppi vatBreakdown). */
  ivaAmount: number;
  /**
   * Breakdown IVA per aliquota. Popolato dal chiamante quando ha accesso alle
   * righe del preventivo (ogni riga puo' avere un vat_rate override).
   * Se assente o lunghezza 1, il PDF puo' mostrare il blocco IVA classico.
   */
  vatBreakdown?: import('./computeVatBreakdown').VatGroup[];

  // ====== Totale finale (IVA inclusa) ======
  totalPrice: number;

  // ====== Margine ======
  /**
   * MARGINE LORDO: ricavo meno i costi DIRETTI (materiali, manodopera,
   * finitura). Non toglie ancora spese generali, sicurezza, trasporto e
   * smaltimento.
   */
  marginGross: number;
  /**
   * MARGINE NETTO: quello che resta davvero, cioe' il lordo meno i costi
   * accessori. Con sconto cliente a zero coincide col "margine industriale".
   */
  marginNet: number;
  /**
   * Le due percentuali sono calcolate SUL RICAVO, non sul costo.
   *
   * Prima la UI faceva `margine / costo` e lo chiamava margine: su un
   * preventivo reale dava 37,5% dove il margine sul ricavo era il 27,3%, e
   * dentro quel numero c'erano 9 punti di spese generali — che sono un costo,
   * non un guadagno. Il margine vero era il 18,2%.
   */
  marginGrossPct: number;
  marginNetPct: number;
}

function pct(base: number, percent: number): number {
  return base * (percent / 100);
}

/**
 * Applica la pipeline di pricing al costo base di un preventivo.
 *
 * Flusso:
 *   costo materiali  + markup materiali %  -> mat_finale
 *   costo manodopera + markup manodopera % -> lab_finale
 *   costo finitura   + markup finitura %   -> fin_finale
 *   subtotale = mat_finale + lab_finale + fin_finale
 *   + overhead % (spese generali)
 *   + safety % (sicurezza/DPI/ponteggi)
 *   + trasporto forfait
 *   + smaltimento forfait
 *   = grossPrice (prezzo lordo)
 *   - sconto cliente %
 *   = netPrice (imponibile IVA esclusa)
 *   + IVA %
 *   = totalPrice (totale fattura)
 *
 * Tutte le percentuali vengono lette da `settings` con fallback a defaults
 * tipici cartongesso (vedi PRICING_DEFAULTS).
 *
 * `customerDiscountOverride` permette di passare uno sconto specifico per il
 * preventivo (es. promo cliente) invece di usare quello default dell'org.
 *
 * `rowSubtotalOverride` sostituisce `costBase + markupTotal` con un valore
 * pre-calcolato per riga (vedi computeRowMarkupSubtotal). Quando passato, il
 * `markupTotal` viene ricalcolato come `override - costBase` e i tre
 * markup categoria vengono ridistribuiti pro-quota sul nuovo totale (per
 * mantenere il dettaglio nelle viste gestionali). Accessori, sconto e IVA
 * si applicano normalmente al nuovo subtotale.
 */
export function applyPricing(
  base: CostBase,
  settings: OrgSettings | null | undefined,
  customerDiscountOverride?: number,
  rowSubtotalOverride?: number | null,
): SellingBreakdown {
  const s = settings ?? {};
  const getPct = (key: keyof typeof PRICING_DEFAULTS, fallback: number): number => {
    const v = s[key as keyof OrgSettings];
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    return fallback;
  };

  const costMaterials = Number(base.materials) || 0;
  const costLabor = Number(base.labor) || 0;
  const costFinish = Number(base.finish) || 0;
  const costBase = costMaterials + costLabor + costFinish;

  // Mark-up (calcolati sempre per audit / pipeline standard)
  const mMat = getPct('markup_materials_pct', PRICING_DEFAULTS.markup_materials_pct);
  const mLab = getPct('markup_labor_pct', PRICING_DEFAULTS.markup_labor_pct);
  const mFin = getPct('markup_finish_pct', PRICING_DEFAULTS.markup_finish_pct);
  let markupMaterials = pct(costMaterials, mMat);
  let markupLabor = pct(costLabor, mLab);
  let markupFinish = pct(costFinish, mFin);
  let markupTotal = markupMaterials + markupLabor + markupFinish;

  // Override: se il chiamante ha calcolato un subtotale per riga (mark-up
  // override per-riga), sostituiamo markupTotal e ridistribuiamo pro-quota
  // i tre markup categoria per mantenere il dettaglio nelle viste gestionali.
  if (rowSubtotalOverride != null && Number.isFinite(rowSubtotalOverride)) {
    const overrideTotal = Math.max(0, Number(rowSubtotalOverride));
    const newMarkupTotal = overrideTotal - costBase;
    const stdMarkup = markupMaterials + markupLabor + markupFinish;
    if (stdMarkup > 0) {
      const ratio = newMarkupTotal / stdMarkup;
      markupMaterials = markupMaterials * ratio;
      markupLabor = markupLabor * ratio;
      markupFinish = markupFinish * ratio;
    } else {
      markupMaterials = newMarkupTotal;
      markupLabor = 0;
      markupFinish = 0;
    }
    markupTotal = newMarkupTotal;
  }

  // Subtotale (costi + markup)
  const subtotal = costBase + markupTotal;

  // Costi accessori
  const oPct = getPct('overhead_pct', PRICING_DEFAULTS.overhead_pct);
  const sPct = getPct('safety_pct', PRICING_DEFAULTS.safety_pct);
  const overhead = pct(subtotal, oPct);
  const safety = pct(subtotal, sPct);
  const transport = Number(s.transport_flat ?? PRICING_DEFAULTS.transport_flat) || 0;
  const disposal = Number(s.disposal_flat ?? PRICING_DEFAULTS.disposal_flat) || 0;
  const accessoriesTotal = overhead + safety + transport + disposal;

  // Prezzo lordo
  const grossPrice = subtotal + accessoriesTotal;

  // Sconto cliente
  const customerDiscountPct = customerDiscountOverride != null
    ? Number(customerDiscountOverride) || 0
    : getPct('default_customer_discount_pct', PRICING_DEFAULTS.default_customer_discount_pct);
  const customerDiscount = pct(grossPrice, customerDiscountPct);

  // Imponibile
  const netPrice = grossPrice - customerDiscount;

  // IVA
  const ivaPct = getPct('iva_pct', PRICING_DEFAULTS.iva_pct);
  const ivaAmount = pct(netPrice, ivaPct);

  // Totale
  const totalPrice = netPrice + ivaAmount;

  // Margine. Il lordo copre i soli costi diretti; il netto toglie anche gli
  // accessori (spese generali, sicurezza, trasporto, smaltimento), che sono
  // costi che l'azienda sostiene davvero e non guadagno.
  const marginGross = netPrice - costBase;
  const marginNet = marginGross - accessoriesTotal;

  const round2 = (n: number) => Math.round(n * 100) / 100;

  return {
    costMaterials: round2(costMaterials),
    costLabor: round2(costLabor),
    costFinish: round2(costFinish),
    costBase: round2(costBase),
    markupMaterials: round2(markupMaterials),
    markupLabor: round2(markupLabor),
    markupFinish: round2(markupFinish),
    markupTotal: round2(markupTotal),
    overhead: round2(overhead),
    safety: round2(safety),
    transport: round2(transport),
    disposal: round2(disposal),
    accessoriesTotal: round2(accessoriesTotal),
    grossPrice: round2(grossPrice),
    customerDiscountPct,
    customerDiscount: round2(customerDiscount),
    netPrice: round2(netPrice),
    ivaPct,
    ivaAmount: round2(ivaAmount),
    totalPrice: round2(totalPrice),
    marginGross: round2(marginGross),
    marginNet: round2(marginNet),
    marginGrossPct: netPrice > 0 ? (marginGross / netPrice) * 100 : 0,
    marginNetPct: netPrice > 0 ? (marginNet / netPrice) * 100 : 0,
  };
}
